import React, { useState, useEffect, useCallback } from 'react';
import {
  FaTachometerAlt, FaStore, FaList, FaBalanceScale,
  FaDownload, FaMoneyBillWave, FaUserFriends, FaMoneyCheckAlt, FaUserTie, FaUserShield, FaHistory, FaExchangeAlt, FaCashRegister, FaArrowCircleLeft, FaUsers, FaChartBar, FaBoxOpen, FaTruck, FaPlus, FaUserPlus
} from 'react-icons/fa';
import { FaShop } from 'react-icons/fa6';
import { FcDebt, FcMoneyTransfer } from 'react-icons/fc';
import { LuNotebookText } from "react-icons/lu";
import { toast } from 'react-toastify';
import Select from 'react-select';
import Loader from '../components/Loader';
import { useAuth } from '../contexts/AuthContext';

const Ruxsatlar = () => {
  const { user, token } = useAuth();

  // --- Store Permission Modal State ---
  const [storeModalOpen, setStoreModalOpen] = useState(false);
  const [storeSearch, setStoreSearch] = useState(null);
  const [stores, setStores] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  const [loadingStores, setLoadingStores] = useState(false);

  // --- Staff Selection State ---
  const [selectedStaff, setSelectedStaff] = useState('');
  const [staffMembers, setStaffMembers] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingPermissions, setPendingPermissions] = useState({});

  // Filtered stores for dropdown
  const filteredStores = stores.filter(
    s =>
      s.name.toLowerCase().includes(storeSearch?.toLowerCase() || '') &&
      !selectedStores.some(sel => sel.id === s.id)
  );

  // Fetch stores for dropdown
  const fetchStores = async () => {
    try {
      setLoadingStores(true);
      const response = await fetch('/api/store-permissions/stores', {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          ...(user?.ownerId ? { 'x-owner-id': String(user.ownerId) } : {}),
          ...(user?.staffId ? { 'x-staff-id': String(user.staffId) } : {}),
        }
      });
      if (!response.ok) throw new Error('Failed to fetch stores');
      const data = await response.json();
      setStores(data);
    } catch (error) {
      console.error('Error fetching stores:', error);
      toast.error('Do\'konlarni yuklab bo\'lmadi');
    } finally {
      setLoadingStores(false);
    }
  };

  // Fetch existing store permissions for selected staff
  const fetchStorePermissions = async (staffId) => {
    try {
      const response = await fetch(`/api/store-permissions/staff/${staffId}`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          ...(user?.ownerId ? { 'x-owner-id': String(user.ownerId) } : {}),
          ...(user?.staffId ? { 'x-staff-id': String(user.staffId) } : {}),
        }
      });
      if (!response.ok) throw new Error('Failed to fetch store permissions');
      const data = await response.json();

      // Convert to selectedStores format
      const permittedStores = data.map(perm => ({
        id: perm.store_id,
        name: perm.store_name,
        importTo: perm.import_to,
        exportFrom: perm.export_from,
        view: perm.view
      }));

      setSelectedStores(permittedStores);
    } catch (error) {
      console.error('Error fetching store permissions:', error);
      toast.error('Do\'kon ruxsatlarini yuklab bo\'lmadi');
    }
  };

  // Open modal and load data
  const handleOpenStoreModal = async () => {
    if (!selectedStaff) {
      toast.error('Iltimos, xodimni tanlang');
      return;
    }

    setStoreModalOpen(true);
    await fetchStores();
    await fetchStorePermissions(selectedStaff);
  };

  // Handle store selection with react-select
  const handleSelectStore = (selectedOption) => {
    if (!selectedOption) return;

    const store = selectedOption.value;
    setSelectedStores(prev => [
      ...prev,
      {
        ...store,
        importTo: false,
        exportFrom: false,
        view: false,
        // Add a unique key for react-select
        value: store.id,
        label: store.name
      }
    ]);

    // Clear the select input
    setStoreSearch(null);
  };

  // Toggle switches
  const handleSwitch = (storeId, field) => {
    setSelectedStores(prev =>
      prev.map(s =>
        s.id === storeId ? { ...s, [field]: !s[field] } : s
      )
    );
  };

  // Remove store and set all permissions to false
  const handleRemoveStore = (storeId) => {
    setSelectedStores(prev =>
      prev.map(s =>
        s.id === storeId
          ? { ...s, removed: true, importTo: false, exportFrom: false, view: false }
          : s
      )
    );
  };

  // Submit modal - save to backend
  const handleStoreSubmit = async () => {
    try {
      if (!selectedStaff) {
        toast.error('Iltimos, xodimni tanlang');
        return;
      }

      // Include both active and removed stores in the request
      const permissions = selectedStores.map(store => ({
        storeId: store.id,
        importTo: store.importTo,
        exportFrom: store.exportFrom,
        view: store.view
      }));

      const response = await fetch('/api/store-permissions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
          ...(user?.ownerId ? { 'x-owner-id': String(user.ownerId) } : {}),
          ...(user?.staffId ? { 'x-staff-id': String(user.staffId) } : {}),
        },
        body: JSON.stringify({
          staffId: selectedStaff,
          permissions
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save store permissions');
      }

      // Only filter out removed stores after successful API call
      setSelectedStores(prev => prev.filter(s => !s.removed));

      toast.success('Do\'kon ruxsatlari muvaffaqiyatli saqlandi');
      setStoreModalOpen(false);
    } catch (error) {
      console.error('Error saving store permissions:', error);
      toast.error(error.message);
    }
  };

  // Update the pages list to match Sidebar.js order and icons
  const allPages = [
    { path: '/', label: 'Dashboard', icon: <FaTachometerAlt /> },
    // Ombor group
    { path: '/omborxona', label: 'Omborxona', icon: <FaPlus /> },
    { path: '/store', label: 'Mahsulotlar', icon: <FaShop /> },
    { path: '/movestore', label: "Ko’chirish", icon: <FaExchangeAlt /> },
    { path: '/inventory', label: 'Inventarizatsiya', icon: <FaList /> },
    // Details group
    { path: '/mahsulotlar', label: 'Mahsulot yaratish', icon: <FaBoxOpen /> },
    { path: '/suppliers', label: 'Taminlovchilar', icon: <FaTruck /> },
    { path: '/kategoriyalar', label: 'Kategoriyalar', icon: <FaList /> },
    { path: '/birliklar', label: 'Birliklar', icon: <FaBalanceScale /> },
    // Singles
    { path: '/xaridlar', label: 'Xaridlar', icon: <FaDownload /> },
    { path: '/kassa', label: 'Kassa', icon: <FaCashRegister /> },
    { path: '/chiqimlar', label: 'Sotuv tarixi', icon: <FaHistory /> },
    { path: '/booking', label: 'Bronlar', icon: <LuNotebookText /> },
    // Qarzdorliklar group
    { path: '/qarzdorliklar', label: 'Mening Qarzim', icon: <FcMoneyTransfer /> },
    { path: '/customer3', label: 'Ulgurchi haridorlar', icon: <FcDebt /> },
    // Mijozlar group
    { path: '/mijozlar', label: 'Mijozlar', icon: <FaUserFriends /> },
    { path: '/credits', label: 'Kreditlar', icon: <FaMoneyCheckAlt /> },
    // Xodimlar group
    { path: '/staffs', label: 'Xodim yaratish', icon: <FaUserPlus /> },
    { path: '/permissions', label: 'Ruxsatlar', icon: <FaUserShield /> },
    // Reports and refunds
    { path: '/hisobotlar', label: 'Hisobotlar', icon: <FaChartBar /> },
    { path: '/refunds', label: 'Qaytarilgan mahsulot', icon: <FaHistory /> },
  ];

  const filteredPages = allPages.filter(page =>
    page.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    page.path.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const splitPagesIntoColumns = (pages) => {
    const halfLength = Math.ceil(pages.length / 2);
    const firstColumn = pages.slice(0, halfLength);
    const secondColumn = pages.slice(halfLength);
    return { firstColumn, secondColumn };
  };

  useEffect(() => {
    const fetchStaff = async () => {
      setLoadingStaff(true);
      try {
        const response = await fetch('/api/staffs', {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            ...(user?.ownerId ? { 'x-owner-id': String(user.ownerId) } : {}),
            ...(user?.staffId ? { 'x-staff-id': String(user.staffId) } : {}),
          }
        });
        if (!response.ok) throw new Error('Failed to fetch staff members');
        const data = await response.json();
        setStaffMembers(data);
      } catch (err) {
        setError(err.message);
        toast.error('Xodimlarni yuklab bo\'lmadi');
      } finally {
        setLoadingStaff(false);
      }
    };

    fetchStaff();
  }, []);

  const handleStaffChange = (e) => {
    setSelectedStaff(e.target.value);
    setPendingPermissions({}); // Reset pending permissions when staff changes
  };

  const fetchPermissions = useCallback(async () => {
    if (!selectedStaff) {
      setPermissions([]);
      return;
    }

    setLoadingPermissions(true);
    try {
      const response = await fetch(`/api/permissions/${selectedStaff}`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          ...(user?.ownerId ? { 'x-owner-id': String(user.ownerId) } : {}),
          ...(user?.staffId ? { 'x-staff-id': String(user.staffId) } : {}),
        }
      });
      if (!response.ok) throw new Error('Failed to fetch permissions');
      const data = await response.json();
      setPermissions(data);
    } catch (error) {
      toast.error('Ruxsatlarni yuklab bo\'lmadi');
      setPermissions([]);
    } finally {
      setLoadingPermissions(false);
    }
  }, [selectedStaff]);

  const handlePermissionChange = (pagePath, isAllowed) => {
    setPendingPermissions(prev => ({
      ...prev,
      [pagePath]: isAllowed
    }));
  };

  const handleSubmitPermissions = async () => {
    if (!selectedStaff) {
      toast.error('Iltimos, xodimni tanlang');
      return;
    }

    try {
      setLoadingPermissions(true);

      // Prepare all permissions for the selected staff member
      const permissionsToSend = allPages.map(page => ({
        staffId: parseInt(selectedStaff),
        pagePath: page.path,
        isAllowed: pendingPermissions[page.path] !== undefined
          ? pendingPermissions[page.path]
          : permissions.find(p => p.page_path === page.path)?.is_allowed || false
      }));

      const response = await fetch('/api/permissions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
          ...(user?.ownerId ? { 'x-owner-id': String(user.ownerId) } : {}),
          ...(user?.staffId ? { 'x-staff-id': String(user.staffId) } : {}),
        },
        body: JSON.stringify({
          staffId: selectedStaff,
          permissions: permissionsToSend
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save permissions');
      }

      const updatedPermissions = await response.json();
      setPermissions(updatedPermissions);
      setPendingPermissions({}); // Clear pending permissions
      toast.success('Ruxsatlarni muvaffaqiyatli saqlandi');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoadingPermissions(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, [selectedStaff, fetchPermissions]);

  // Helper function to get current permission state for a page
  const getCurrentPermission = (pagePath) => {
    // First check pending changes
    if (pendingPermissions[pagePath] !== undefined) {
      return pendingPermissions[pagePath];
    }
    // Then check existing permissions
    const existingPermission = permissions.find(p => p.page_path === pagePath);
    return existingPermission ? existingPermission.is_allowed : false;
  };

  return (
    <div className="container mt-4">
      {error && <div className="alert alert-danger">{error}</div>}

      <select
        className="form-select mb-3"
        value={selectedStaff}
        onChange={handleStaffChange}
        disabled={loadingStaff}
      >
        <option value="">Xodimni tanlang</option>
        {staffMembers.map(staff => (
          <option key={staff.id} value={staff.id}>
            {staff.full_name}
          </option>
        ))}
      </select>

      <div className="mb-3 d-flex align-items-center gap-2">
        <div className="input-group flex-grow-1">
          <span className="input-group-text">
            <i className="bi bi-search"></i>
          </span>
          <input
            type="text"
            className="form-control"
            placeholder="Sahifa nomi yoki manzili bo'yicha qidiruv"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button
          className="btn btn-outline-success ms-2"
          type="button"
          onClick={handleOpenStoreModal}
          disabled={!selectedStaff}
        >
          Do'kon ruxsatlari
        </button>
      </div>

      {loadingPermissions ? (
        <Loader />
      ) : (
        <div className='table-responsive shadow rounded-4'>
          <table className="table table-bordered">
            <tbody>
              {(() => {
                const { firstColumn, secondColumn } = splitPagesIntoColumns(filteredPages);
                return firstColumn.map((page, index) => (
                  <tr key={index}>
                    <td>
                      {page && (
                        <div className="d-flex align-items-center justify-content-between">
                          <span>
                            <span className="me-2">{page.icon}</span>
                            {page.label}
                          </span>
                          <div className="form-check form-switch ">
                            <input
                              className="form-check-input border-dark"
                              type="checkbox"
                              checked={getCurrentPermission(page.path)}
                              onChange={(e) => handlePermissionChange(page.path, e.target.checked)}
                              disabled={!selectedStaff}
                            />
                          </div>
                        </div>
                      )}
                    </td>
                    <td>
                      {secondColumn[index] && (
                        <div className="d-flex align-items-center justify-content-between">
                          <span>
                            <span className="me-2">{secondColumn[index].icon}</span>
                            {secondColumn[index].label}
                          </span>
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input border-dark"
                              type="checkbox"
                              checked={getCurrentPermission(secondColumn[index].path)}
                              onChange={(e) => handlePermissionChange(secondColumn[index].path, e.target.checked)}
                              disabled={!selectedStaff}
                            />
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        </div>
      )}

      <button
        className="btn btn-primary mt-3"
        onClick={handleSubmitPermissions}
        disabled={loadingPermissions || !selectedStaff}
      >
        {loadingPermissions ? 'Saqlanmoqda...' : 'Ruxsatlarni saqlash'}
      </button>

      {/* Store Permission Modal */}
      <div className={`modal fade ${storeModalOpen ? 'show d-block' : ''}`} tabIndex="-1" style={{ background: storeModalOpen ? 'rgba(0,0,0,0.5)' : 'none' }}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Do'konlarga ruxsat berish</h5>
              <button type="button" className="btn-close" onClick={() => setStoreModalOpen(false)}></button>
            </div>
            <div className="modal-body">
              {/* Replace the search input with react-select */}
              <Select
                value={storeSearch}
                onChange={handleSelectStore}
                options={filteredStores.map(store => ({
                  value: store,
                  label: store.name,
                }))}
                onInputChange={(inputValue) => setStoreSearch(inputValue)}
                placeholder="Do'kon qo'shish"
                isClearable
                isSearchable
                noOptionsMessage={() => "Do'kon topilmadi"}
                className="mb-3"
                classNamePrefix="select"
                isLoading={loadingStores}
                loadingMessage={() => "Yuklanmoqda..."}
              />
              {/* Selected Stores List */}
              <div>
                {selectedStores.length === 0 && <div className="text-muted">Do'kon tanlanmagan</div>}
                {selectedStores.map(store => (
                  <div key={store.id} className="d-flex align-items-center justify-content-between border rounded p-2 mb-2 bg-light">
                    <span>{store.name}</span>
                    <div className="d-flex align-items-center gap-3">
                      <div className="form-check form-switch me-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`importTo-${store.id}`}
                          checked={store.importTo}
                          onChange={() => handleSwitch(store.id, 'importTo')}
                        />
                        <label className="form-check-label small" htmlFor={`importTo-${store.id}`}>Import to</label>
                      </div>
                      <div className="form-check form-switch me-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`exportFrom-${store.id}`}
                          checked={store.exportFrom}
                          onChange={() => handleSwitch(store.id, 'exportFrom')}
                        />
                        <label className="form-check-label small" htmlFor={`exportFrom-${store.id}`}>Export from</label>
                      </div>
                      <div className="form-check form-switch me-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`view-${store.id}`}
                          checked={store.view}
                          onChange={() => handleSwitch(store.id, 'view')}
                        />
                        <label className="form-check-label small" htmlFor={`view-${store.id}`}>View</label>
                      </div>
                      <button
                        className="btn btn-sm btn-danger"
                        title="Bekor qilish"
                        onClick={() => handleRemoveStore(store.id)}
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setStoreModalOpen(false)}>Yopish</button>
              <button className="btn btn-primary" onClick={handleStoreSubmit}>Saqlash</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Ruxsatlar;